import { ChangeDetectorRef, Directive, inject, InjectionToken, OnInit } from '@angular/core';

import { I18nService } from '@recruitee/i18n';
import { IntegrationsApiService } from '@recruitee/integrations-http';
import { DialogRef } from '@recruitee/user-interface';
import { objectKeys } from '@recruitee/utils';

import { trimmedFormValue } from '../../common/utils';
import { DynamicForm } from '../../dynamic/inputs/dynamic-form/dynamic-form';
import { DynamicFormEventService } from '../../dynamic/inputs/dynamic-form/dynamic-form-event.service';

export enum IntegrationsActionStrategy {
  INTEGRATE,
  CHANGE_CREDENTIALS,
}

export type IntegrateStrategyActions = {
  dynamicForm: DynamicForm;
  send: () => void;
  /**
   * Custom description for form defined by {@link IntegrationsActionStrategy}
   * when defined used instead of {@link IntegrateDialogTemplate.description}
   */
  customFormDescription?: string;
};

@Directive({
  providers: [DynamicFormEventService],
})
export abstract class IntegrateDialogTemplate<
  StrategyMap extends IntegrationsActionStrategy = IntegrationsActionStrategy,
  TDialogResponse = any,
> implements OnInit
{
  public abstract heading: string;
  public abstract description: string;
  public abstract helpArticle: string | null;
  public abstract api: IntegrationsApiService;
  public saveButtonText: string;
  public strategy: IntegrateStrategyActions | undefined;
  public abstract strategyActions: Partial<Record<StrategyMap, IntegrateStrategyActions>>;
  public loading = false;
  public errorFields = {};
  public errors: string[] = [];
  public i18n: I18nService = inject(I18nService);
  public saveButtonTexts: Partial<Record<StrategyMap | IntegrationsActionStrategy, string>> = {
    [IntegrationsActionStrategy.INTEGRATE]: this.i18n.translate(
      'rti.common.settings.integrations.integrate',
    ),
    [IntegrationsActionStrategy.CHANGE_CREDENTIALS]: this.i18n.translate('rti.common.save'),
  };
  protected dialogRef = inject(DialogRef);
  protected cdRef = inject(ChangeDetectorRef);

  protected constructor(
    protected strategyDictionaryKey: StrategyMap,
    public skipTranslations: boolean = false,
  ) {}

  public get form(): DynamicForm | undefined {
    return this.strategy?.dynamicForm;
  }

  public get canSave(): boolean {
    return !!this.form?.valid && !this.loading;
  }

  public closeDialog() {
    this.dialogRef.close();
  }

  public save(): void {
    if (!this.canSave) return;

    this.strategy!.send();

    this.loading = true;
    this.errorFields = {};
    this.errors = [];
    this.cdRef.detectChanges();
  }

  public dialogResponse(response: TDialogResponse) {
    this.dialogRef.close(response);
  }

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method,@typescript-eslint/no-empty-function
  public ngOnDestroy() {}

  public ngOnInit(): void {
    this.setAction();
    this.setButtonText();
  }

  public integrate(attachToErrors: boolean = false): void {
    this.api.post('integration', trimmedFormValue(this.form?.value)).subscribe(
      ({ integration }: { integration: TDialogResponse }) => {
        this.dialogResponse(integration);
      },
      ({ error }) => {
        const errorFields = error && error.errorFields;

        if (
          errorFields &&
          objectKeys(errorFields).some(error =>
            objectKeys(this.form!.group.controls).some(input => input === error),
          )
        ) {
          this.errorFields = errorFields;

          Object.keys(error.errorFields).forEach(key => {
            this.form?.group.get(key)!.setErrors({ [key]: true });
          });
        } else {
          this.errors = error.error;
        }

        if (attachToErrors) {
          this.errors = error.error;
        }

        this.loading = false;
        this.cdRef.detectChanges();
      },
    );
  }

  public changeCredentials(): void {
    this.api.patch('integration/update-credentials', trimmedFormValue(this.form?.value)).subscribe(
      (data: { integration: TDialogResponse }) => {
        this.form?.group.markAsPristine();
        this.dialogResponse(data.integration);
      },
      ({ error }) => {
        this.errors = error.error;
        this.loading = false;
        this.cdRef.detectChanges();
      },
    );
  }

  private setAction(): void {
    this.strategy = this.strategyActions[this.strategyDictionaryKey];
  }

  private setButtonText(): void {
    this.saveButtonText =
      this.saveButtonTexts[this.strategyDictionaryKey] || this.i18n.translate('rti.common.save');
  }
}

export const IntegrateDialogFactory = new InjectionToken<() => IntegrateDialogTemplate>(
  'IntegrateDialogAbstract factory',
);
